import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Divider,
  IconButton,
  TextareaAutosize,
  makeStyles,
} from "@material-ui/core";
// import GlobalStyles from "../shared/helpers/GlobalStyles";

import { FieldArray, Form, Formik } from "formik";
import { Endpoint } from "../../api/base";
import { generateQuickGuid, getAccountingJson } from "../shared/helpers/helper";

import InvoiceBreakdownTable from "./InvoiceBreakdownTable";
import InvoiceDetailsTable from "./InvoiceDetailsTable";
import AccountingField from "../../components/shared/AccountingField";
import AccountingHeader from "./AccountingHeader";

import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@material-ui/icons/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveAndGenerateButton from "./SaveAndGenerateButton";
import { useToast } from "../../context/ToastContext";
import { tokens } from "../../theme/tokens";

const useStyle = makeStyles(() => ({
  page: {
    padding: `${tokens.space[5]} 2% ${tokens.space[6]} 2%`,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.space[3],
    marginBottom: tokens.space[5],
  },
  toolbarTitle: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.space[3],
  },
  pageHeading: {
    margin: "0px",
    fontSize: "1.5rem",
    fontWeight: 600,
    color: tokens.color.text.primary,
  },
  pageCount: {
    fontSize: "0.875rem",
    color: tokens.color.text.secondary,
  },
  newInvoiceButton: {
    height: "40px",
    fontSize: "0.9rem",
    paddingLeft: tokens.space[4],
    paddingRight: tokens.space[4],
    color: tokens.color.brand.onPrimary,
    backgroundColor: tokens.color.brand.primaryAction,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: tokens.radius.sm,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: tokens.color.brand.primaryAction,
      opacity: "0.9",
      boxShadow: "none",
    },
  },
  card: {
    position: "relative",
    overflow: "hidden",
    border: `1px solid ${tokens.color.border.default}`,
    borderLeft: `3px solid ${tokens.color.brand.primary}`,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.color.surface.default,
    marginBottom: tokens.space[5],
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.space[3],
    padding: `${tokens.space[3]} ${tokens.space[4]}`,
    backgroundColor: tokens.color.surface.sunken,
    borderBottom: `1px solid ${tokens.color.border.default}`,
  },
  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.space[3],
  },
  invoiceTitle: {
    margin: "0px",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: tokens.color.text.secondary,
  },
  invoiceIdChip: {
    margin: "0px",
    fontSize: "0.8rem",
    fontFamily: tokens.type.family,
    color: tokens.color.text.primary,
    backgroundColor: tokens.color.surface.default,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.full,
    padding: `2px ${tokens.space[3]}`,
  },
  subjectSummary: {
    fontSize: "0.85rem",
    color: tokens.color.text.secondary,
  },
  cardBody: {
    padding: tokens.space[5],
    overflowX: "auto",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "flex-end",
    padding: `${tokens.space[4]} ${tokens.space[5]}`,
    borderTop: `1px solid ${tokens.color.border.default}`,
  },
  accountingHeaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignSelf: "center",
    marginBottom: tokens.space[5],
  },
  iconStyles: {
    marginRight: "5px",
  },
  deleteInvoice: {
    border: `1px solid ${tokens.color.border.default}`,
    backgroundColor: tokens.color.surface.default,
    "&:hover": {
      color: tokens.color.status.error,
      borderColor: tokens.color.status.error,
    },
  },
  notesSection: {
    marginTop: tokens.space[5],
  },
  notesLabel: {
    margin: `0px 0px ${tokens.space[2]} 0px`,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: tokens.color.text.secondary,
  },
  textAreaInput: {
    "& .MuiInputBase-input": {
      textIndent: "10px",
      textAlign: "left",
    },
    fontSize: "0.9rem",
    fontFamily: tokens.type.family,
    padding: tokens.space[3],
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${tokens.color.border.default}`,
    color: `${tokens.color.text.primary} !important`,
    borderRadius: tokens.radius.sm,
    backgroundColor: tokens.color.surface.sunken,
    resize: "vertical",
    "&:focus": {
      outline: "none",
      borderColor: tokens.color.brand.primary,
    },
  },
}));

function Accounting({
  invoices,
  caseData,
  setInvoices,
  setGeneratePDF,
  setInvoiceIndex,
  clientRates,
}) {
  const classes = useStyle();
  const formRef = useRef();
  const { showToast } = useToast();

  // handle generate pdf button click
  const handleGeneratePDF = (values, index) => {
    // console.log("hereeee");
    handleSaveInvoice(values, index);
    setInvoices(values.invoices);
    setGeneratePDF(true);
    setInvoiceIndex(index);
  };

  // handle save invoice button click
  const handleSaveInvoice = (values, index) => {
    Endpoint.saveInvoices(values.invoices[index])
      .then((res) => {
        // console.log(res);
        showToast("Profile updated successfully", "success");
      })
      .catch((err) => {
        console.log(err);
        showToast("Sorry something went wrong, please try again later", "error");
      });
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{ invoices: invoices }}
        style={{ height: "100%" }}
        // validateOnBlur={true}
        // validateOnChange
        // validationSchema={CasesValidateSchema}
        innerRef={formRef}
        onSubmit={(e) => {
          console.log("submitting", e);
          // handleSubmit(values);
          // resetForm();
        }}
        render={({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <FieldArray name="invoices">
              {({ push, remove }) => (
                <div className={classes.page}>
                  <div className={classes.toolbar}>
                    <div className={classes.toolbarTitle}>
                      <h2 className={classes.pageHeading}>Invoices</h2>
                      <span className={classes.pageCount}>
                        {values.invoices.length}{" "}
                        {values.invoices.length === 1 ? "invoice" : "invoices"}
                      </span>
                    </div>
                    <Button
                      className={classes.newInvoiceButton}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        push(
                          getAccountingJson(
                            caseData.case_detail.file_no,
                            caseData.case_detail.case_details.case_information,
                            caseData.case_detail.case_details
                              .client_information,
                            true,
                            clientRates
                          )
                        );
                      }}
                    >
                      <AddIcon className={classes.iconStyles} /> New Invoice
                    </Button>
                  </div>

                  {values.invoices.map((invoice, index) => {
                    return (
                      <div className={classes.card} key={index}>
                        <div className={classes.cardHeader}>
                          <div className={classes.cardHeaderLeft}>
                            <p className={classes.invoiceTitle}>Invoice</p>
                            <span className={classes.invoiceIdChip}>
                              {values.invoices[index].invoice_id}
                            </span>
                            {values.invoices[index].subject &&
                              values.invoices[index].subject.trim() && (
                                <span className={classes.subjectSummary}>
                                  {values.invoices[index].subject}
                                </span>
                              )}
                          </div>
                          {index > 0 && (
                            <IconButton
                              className={classes.deleteInvoice}
                              onClick={() => {
                                remove(index);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </div>
                        <div className={classes.cardBody}>
                          <div className={classes.accountingHeaderContainer}>
                            <AccountingHeader values={values} index={index} />
                          </div>
                          <div>
                            <InvoiceBreakdownTable
                              values={values}
                              index={index}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                          <div style={{ marginTop: tokens.space[5] }}>
                            <InvoiceDetailsTable
                              values={values}
                              index={index}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                          <div className={classes.notesSection}>
                            <p className={classes.notesLabel}>
                              Accounting Notes
                            </p>
                            <TextareaAutosize
                              maxRows={5}
                              minRows={5}
                              aria-label="Accounting notes"
                              placeholder="Add notes for this invoice…"
                              defaultValue=""
                              value={values.invoices[index].accounting_notes}
                              onChange={(e) => {
                                setFieldValue(
                                  `invoices[${index}].accounting_notes`,
                                  e.target.value
                                );
                              }}
                              className={classes.textAreaInput}
                            />
                          </div>
                        </div>
                        <div className={classes.cardFooter}>
                          <SaveAndGenerateButton
                            values={values}
                            index={index}
                            setFieldValue={setFieldValue}
                            handleGeneratePDF={handleGeneratePDF}
                            handleSaveInvoice={handleSaveInvoice}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </FieldArray>
          </Form>
        )}
      />
    </>
  );
}

export default Accounting;
